/**
 * Apartsales — Toast system.
 *
 * iOS 26-style top-floating toast для acknowledgement действий из
 * action sheet'ов и других мест UI. Канонический pattern:
 *
 *   - Появляется сверху под TopBar (или под Dynamic Island на iPhone 14+)
 *   - Compact pill ~36px height, blur regular glass background
 *   - Auto-dismiss через 2.6s (Apple default)
 *   - Spring slide-from-top, fade-out
 *   - Поддерживает icon + text, опционально trailing action
 *   - Queue: если приходит новый toast пока показывается старый — стек
 *     до 3, остальное теряется (Apple behaviour, не блокирует UI)
 *
 * API:
 *   import { showToast } from './toast';
 *   showToast({ icon: <CheckIcon/>, label: 'Ссылка скопирована' });
 *   showToast({ kind: 'success', label: 'Сохранено' });
 *
 * Mount once: <ToastHost /> в AppShell. Управляется через Zustand store.
 */

import { create } from 'zustand';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { CheckGlyph } from './action-glyphs';

export type ToastKind = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface ToastItem {
  id: string;
  label: string;
  sublabel?: string;
  kind?: ToastKind;
  icon?: React.ReactNode;
  /** Duration ms. Default 2600. */
  durationMs?: number;
}

interface ToastState {
  items: ToastItem[];
  show: (item: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastState>((set) => ({
  items: [],
  show: (item) => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    set((s) => ({
      items: [...s.items, { id, durationMs: 2600, ...item }].slice(-3),
    }));
  },
  dismiss: (id) =>
    set((s) => ({
      items: s.items.filter((t) => t.id !== id),
    })),
}));

export function showToast(item: Omit<ToastItem, 'id'>) {
  useToastStore.getState().show(item);
}

/**
 * ToastHost — единый mount-point. Размещается в AppShell ОДИН раз.
 * Floating сверху с учётом safe-area-top + room для Dynamic Island.
 */
export function ToastHost() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 z-[70] flex flex-col items-center gap-2 px-3"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 10px)' }}
    >
      <AnimatePresence>
        {items.map((item) => (
          <ToastPill key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastPill({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, item.durationMs ?? 2600);
    return () => clearTimeout(t);
  }, [item.durationMs, onDismiss]);

  const tone = toneFor(item.kind ?? 'default');

  return (
    <motion.div
      role="status"
      onClick={onDismiss}
      className="pointer-events-auto flex items-center gap-2.5 rounded-full px-4 py-2.5"
      style={{
        background: 'var(--color-header-blur)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        boxShadow: '0 8px 24px -8px rgba(0,0,0,0.18), 0 0 0 0.5px var(--color-glass-stroke)',
        maxWidth: 'min(calc(100% - 24px), 440px)',
      }}
      initial={{ y: -40, opacity: 0, scale: 0.96 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -40, opacity: 0, scale: 0.96 }}
      transition={{ type: 'spring', damping: 26, stiffness: 360 }}
    >
      <span className="grid h-5 w-5 shrink-0 place-items-center" style={{ color: tone.iconColor }}>
        {item.icon ?? defaultIconFor(item.kind ?? 'default')}
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-[14px] font-semibold text-label">{item.label}</span>
        {item.sublabel && (
          <span className="text-[12px] font-normal text-label-2">{item.sublabel}</span>
        )}
      </div>
    </motion.div>
  );
}

function toneFor(kind: ToastKind) {
  switch (kind) {
    case 'success': return { iconColor: '#34C759' };  // iOS systemGreen
    case 'warning': return { iconColor: '#FF9F0A' };  // iOS systemOrange
    case 'error':   return { iconColor: '#FF3B30' };  // iOS systemRed
    case 'info':    return { iconColor: 'var(--color-brand-blue)' };
    default:        return { iconColor: 'var(--color-label)' };
  }
}

function defaultIconFor(kind: ToastKind) {
  switch (kind) {
    case 'success': return <CheckGlyph />;
    case 'warning':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 2L1 21h22L12 2z M12 9v6 M12 18v0.1"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'error':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 9l6 6 M15 9l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'info':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 11v5 M12 8v0.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}
